'use client';
import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import Sidebar from './Sidebar';
import { Menu, ShieldOff } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { hasPermission, ROUTE_PERMISSIONS } from '@/lib/rbac/permissions';
import type { Permission } from '@/lib/rbac/permissions';

interface AppLayoutProps {
  children: React.ReactNode;
  activeRoute?: string;
}

export default function AppLayout({ children, activeRoute }: AppLayoutProps) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const { user, loading, role } = useAuth();
  const router = useRouter();

  // Zero Trust: redirect unauthenticated users
  useEffect(() => {
    if (!loading && !user) {
      router.replace('/sign-up-login-screen');
    }
  }, [user, loading, router]);

  // Zero Trust: check route-level permission
  const routePermission = activeRoute ? (ROUTE_PERMISSIONS[activeRoute] as Permission | undefined) : undefined;
  const routeAllowed = !routePermission || hasPermission(role, routePermission);

  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (mobile) setSidebarCollapsed(true);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleOverlayClick = () => setMobileOpen(false);

  if (loading || !user) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100dvh',
          backgroundColor: 'var(--bg-primary)',
        }}
      >
        <div
          style={{
            width: '28px',
            height: '28px',
            border: '2px solid var(--border)',
            borderTop: '2px solid var(--accent-blue)',
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite',
          }}
        />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        height: '100dvh',
        overflow: 'hidden',
        backgroundColor: 'var(--bg-primary)',
        position: 'relative',
      }}
    >
      {/* Mobile overlay */}
      {isMobile && mobileOpen && (
        <div
          className="sidebar-overlay"
          onClick={handleOverlayClick}
          style={{ zIndex: 40 }}
        />
      )}

      {/* Sidebar */}
      <div
        style={{
          position: isMobile ? 'fixed' : 'relative',
          top: 0,
          left: 0,
          height: '100%',
          zIndex: isMobile ? 50 : 'auto',
          transform: isMobile && !mobileOpen ? 'translateX(-100%)' : 'translateX(0)',
          transition: 'transform 280ms cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        <Sidebar
          collapsed={isMobile ? false : sidebarCollapsed}
          onToggle={() => {
            if (isMobile) setMobileOpen(false);
            else setSidebarCollapsed(!sidebarCollapsed);
          }}
          activeRoute={activeRoute}
        />
      </div>

      {/* Main content */}
      <main
        style={{
          flex: 1,
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
        }}
      >
        {/* Mobile top bar */}
        {isMobile && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '0 16px',
              height: '56px',
              backgroundColor: 'var(--sidebar-bg)',
              borderBottom: '1px solid var(--sidebar-border)',
              position: 'sticky',
              top: 0,
              zIndex: 30,
            }}
          >
            <button
              onClick={() => setMobileOpen(true)}
              style={{
                background: 'none',
                border: '1px solid var(--border)',
                borderRadius: '7px',
                padding: '6px',
                cursor: 'pointer',
                color: 'var(--text-muted)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Menu size={16} />
            </button>
            <span
              style={{
                fontSize: '14px',
                fontWeight: 700,
                color: 'var(--text-primary)',
                letterSpacing: '-0.01em',
              }}
            >
              Nexus
            </span>
          </div>
        )}
        <div style={{ flex: 1, overflow: 'auto' }}>
          {/* Zero Trust: block access if role lacks permission for this route */}
          {!routeAllowed ? (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                minHeight: '400px',
                gap: '16px',
                color: 'var(--text-muted)',
              }}
            >
              <ShieldOff size={48} style={{ color: 'var(--accent-red)', opacity: 0.5 }} />
              <div style={{ fontSize: '18px', fontWeight: 700, color: 'var(--text-primary)' }}>
                Access Denied
              </div>
              <div style={{ fontSize: '14px', color: 'var(--text-muted)', textAlign: 'center', maxWidth: '360px', lineHeight: 1.6 }}>
                Your role (<strong style={{ color: 'var(--text-secondary)' }}>{role}</strong>) does not have
                permission to access this page. Contact your administrator to request access.
              </div>
            </div>
          ) : (
            children
          )}
        </div>
      </main>
    </div>
  );
}